import { useEffect, useRef, useState } from "react";
import { motion, animate } from "framer-motion";
import { logEvent } from "@/lib/analytics";
import { fmtCr, fmtNum } from "@/lib/format";
import { TrendUp, ArrowRight, Handshake, Warning } from "@phosphor-icons/react";

// Moment 2 — hero "Money Stuck" reveal. A rapid count-up to the recoverable figure
// over a blurred backdrop, one punchy line, and a single green CTA into the dashboard.
export function MoneyStuckReveal({ amount, settlementCount, followupCount, onEnter }) {
  const [val, setVal] = useState(0);
  const btnRef = useRef(null);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    logEvent("sample_reveal_shown", { amount });
    if (reduce) { setVal(amount); return; }
    const controls = animate(0, amount || 0, {
      duration: 1.4, ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [amount]);

  useEffect(() => { const t = setTimeout(() => btnRef.current?.focus(), 700); return () => clearTimeout(t); }, []);

  return (
    <motion.div
      data-testid="money-stuck-reveal"
      className="fixed inset-0 z-[125] flex items-center justify-center p-4 bg-[#0B3D91]/40"
      style={{ backdropFilter: "blur(10px)" }}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      role="dialog" aria-modal="true" aria-label="Recovery opportunity found"
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 20 }}
        className="relative w-full max-w-lg bg-white border border-gray-200 shadow-2xl rounded-sm p-9 md:p-11 text-center overflow-hidden"
      >
        <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-[#16A34A]/10 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 border border-[#16A34A]/30 bg-[#16A34A]/5 text-[#16A34A]">
            <TrendUp size={14} weight="bold" />
            <span className="text-[11px] font-bold uppercase tracking-[0.2em]">Recovery opportunity found</span>
          </div>

          <div className="mt-6 text-5xl md:text-6xl font-black tracking-tighter text-[#0B3D91] leading-none tabular-nums"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontVariantNumeric: "tabular-nums" }}
            data-testid="reveal-amount">
            {fmtCr(Math.round(val))}
          </div>
          <p className="mt-4 text-base md:text-lg text-gray-600 leading-relaxed max-w-sm mx-auto"
            style={{ fontFamily: "'Inter', sans-serif" }}>
            is recoverable in this sample portfolio. Here's exactly what to chase first.
          </p>

          <div className="mt-6 grid grid-cols-2 gap-3 text-left">
            <div className="border border-gray-200 p-3">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500"><Handshake size={13} weight="bold" className="text-[#7C3AED]" /> Settlement candidates</div>
              <div className="mt-1 text-2xl font-black text-gray-900 tabular-nums" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{fmtNum(settlementCount)}</div>
            </div>
            <div className="border border-gray-200 p-3">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500"><Warning size={13} weight="bold" className="text-[#D97706]" /> Need follow-up</div>
              <div className="mt-1 text-2xl font-black text-gray-900 tabular-nums" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{fmtNum(followupCount)}</div>
            </div>
          </div>

          <button
            ref={btnRef}
            onClick={() => { logEvent("sample_reveal_cta", {}); onEnter(); }}
            data-testid="reveal-enter-btn"
            className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#16A34A] text-white rounded-sm px-8 py-3.5 font-bold text-base hover:bg-[#138a3e] hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
          >
            Show me what's stuck <ArrowRight size={18} weight="bold" />
          </button>
          <p className="mt-3 text-xs text-gray-400">Explore freely — then import your own portfolio in 2 minutes.</p>
        </div>
      </motion.div>
    </motion.div>
  );
}
